import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useRealKmClient } from '../../../lib/useRealKmClient.js';
import { useAuth as useRealAuth } from '../../../context/AuthContext';
import { canManageEsperienze } from '../../../lib/roles.js';
import { PREAVVISI, PREAVVISI_PROPOSTI, anteprimaPromemoria, formatDataIt } from '../preavvisi.js';
import { useFiltriEsperienze } from '../hooks/useTassonomie.js';

/* Corretto sui campi obbligatori confermati dal collaudo reale
   (Collaudo_KM_TalentGPT_20260907_3.pdf, aggiornato 18/09 — Appendice
   F.4): consulente_id, ruolo, cliente_finale, denominazione_progetto,
   settore_l1, settore_l2, dominio_l1, dominio_l2, data_inizio,
   data_fine_prevista, capacita_garantita, attivita_svolte,
   tariffa_applicata, referente_commerciale — 14 campi ora, non più 12.

   Le ultime due sono nuove: aggiunte dalla migrazione 018
   (campi_staffing_esperienza), che l'Appendice F.4 lega esplicitamente
   alla tabella "Campi della scheda esperienza" dell'FRD KM v1.0 di
   Daniele Turra — lo stesso documento che avevamo confrontato prima,
   dove risultavano mancanti. referente_commerciale era già nel form come
   facoltativo (nessuna conferma all'epoca): ora promosso a obbligatorio.
   tariffa_applicata è nuovo — l'FRD lo descrive "Sistema (sola lettura da
   staffing)", quindi qui è un valore che HR inserisce se già lo conosce,
   non calcolato da noi.

   Prima mancavano settore_l2 e dominio_l2 (mai richiesti), e due campi
   avevano nomi diversi: "attivita" -> "attivita_svolte",
   "capacita_percentuale" -> "capacita_garantita". Ogni apertura avrebbe
   dato 422 sul reale.

   main_contractor/contesto_contrattuale/tecnologie/note/
   preavvisi_reminder restano opzionali com'erano — nessuna conferma che
   esistano davvero lato backend, ma erano già marcati non obbligatori
   quindi il rischio è nullo se ignorati.

   Ricerca inline (18/09): nessun endpoint di ricerca per candidati per
   nome, né una tassonomia settore/dominio esposta via API (verificato,
   non trovato nel collaudo) — quindi "Risorsa", "Settore", "Dominio" e
   "Ruolo" restano campi liberi, non è possibile costruire un suggerimento
   vero senza inventarlo. "Cliente finale" invece sì: i nomi reali già
   usati si possono leggere da GET /esperienze (che già chiamiamo altrove)
   e proporli come suggerimento via <datalist> nativo del browser.

   RISORSA — 27/09: il campo "Risorsa (identificativo)" chiedeva l'uuid
   del consulente a mano. Ora è una ricerca live per nome/email: l'elenco
   arriva da GET /hr/sa-stato (consulenti tracciati — lo stesso usato
   dalla scheda "Consulenti già staffati" della Console HR, già
   verificato), caricato una volta sola e filtrato in memoria mentre si
   digita. Si vede nome e cognome, l'uuid viaggia solo nel payload.
   Arrivando da "+ Apri nuova esperienza" (?consulente_id=...) il campo
   resta bloccato, ma mostra il nome risolto dallo stesso elenco invece
   dell'uuid. Tolta la vecchia chiamata GET /resources/{consulente_id}:
   passava un id di consulente a un endpoint che vuole un id di
   candidato, quindi il nome non arrivava quasi mai. */

/* 28/09 — allineamento all'AF v2.0 (UC-P03-01 e tabella «Campi della
   scheda esperienza»):
   - il payload usa i nomi del contratto AperturaEsperienzaIn: prima
     `preavvisi_reminder` (testo) e `note` venivano scartati dal backend, che
     applicava sempre i preavvisi 30/7 e perdeva le note;
   - settore e dominio si scelgono dalla tassonomia SEC-/DOM- di
     GET /esperienze/filtri e viaggiano come codici: la lista UC-KM-01 filtra
     per codice, e il testo libero non veniva mai trovato;
   - il dominio funzionale è multiplo («almeno uno, multiplo»): il primo va in
     dominio_l1/dominio_l2 (contratto attuale), l'elenco completo in
     `domini_funzionali`, in attesa che il backend lo persista;
   - la tariffa applicata è «Sistema (sola lettura da staffing)»: non si
     digita più, la valorizza il backend dallo staffing collegato;
   - preavvisi proposti un mese e una settimana prima (passo 4);
   - controllo di esperienza già aperta per la stessa risorsa-progetto prima
     del salvataggio (flusso alternativo 2);
   - main contractor e contesto contrattuale tolti: l'AF li rinvia alla fase 2
     (modello piatto, §6 Esperienza e Next Steps). */
const REQUIRED_FIELDS = [
  'consulente_id',
  'ruolo',
  'cliente_finale',
  'denominazione_progetto',
  'settore_l1',
  'settore_l2',
  'data_inizio',
  'data_fine_prevista',
  'capacita_garantita',
  'attivita_svolte',
  'referente_commerciale',
];

const MAX_SUGGERIMENTI = 8;

// Confronto senza maiuscole né accenti: "nicolo" trova "Nicolò".
function normalizza(t) {
  return (t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/* 16/10 — UAT-12 (UC-P03-01): da «Knowledge Management» si potevano
   scegliere solo le risorse che avevano già un'anagrafica consulente, cioè
   già un'esperienza; dal pannello della risorsa invece l'apertura funzionava
   per qualunque Proponibile. Ora l'elenco comprende anche le risorse in R3
   senza consulente (GET /hr/sa-da-sollecitare), con chiave «cand:<id>».
   L'anagrafica consulente si crea solo al salvataggio, con lo stesso
   POST /resources/{id}/consulente del pannello (idempotente): scegliere una
   risorsa e poi annullare non lascia dati. */
const PREFISSO_CANDIDATO = 'cand:';

/* Ricerca risorsa con suggerimenti live. Tastiera: frecce per muoversi,
   Invio per scegliere, Esc per chiudere. Modificare il testo dopo una
   scelta la annulla (consulente_id torna vuoto): il form non può partire
   con un nome scritto a mano che non corrisponde a nessun consulente. */
function RisorsaSearch({ consulenti, stato, selectedId, onSelect }) {
  const selezionato = consulenti.find((c) => c.consulente_id === selectedId) || null;
  const [query, setQuery] = useState(selezionato?.nome || '');
  const [aperto, setAperto] = useState(false);
  const [evidenziato, setEvidenziato] = useState(0);
  const chiudiTimer = useRef(null);

  // Se la selezione arriva dall'esterno (es. elenco caricato dopo), allinea il testo.
  useEffect(() => {
    if (selezionato && !query) setQuery(selezionato.nome || selezionato.email || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selezionato]);

  const q = normalizza(query.trim());
  const risultati = q
    ? consulenti.filter((c) => normalizza(c.nome).includes(q) || normalizza(c.email).includes(q)).slice(0, MAX_SUGGERIMENTI)
    : [];

  function scegli(c) {
    onSelect(c.consulente_id);
    setQuery(c.nome || c.email || c.consulente_id);
    setAperto(false);
  }

  function onKeyDown(e) {
    if (!aperto || risultati.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setEvidenziato((i) => Math.min(i + 1, risultati.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setEvidenziato((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); scegli(risultati[evidenziato]); }
    else if (e.key === 'Escape') { setAperto(false); }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        id="consulente_id"
        className="field-input"
        autoComplete="off"
        role="combobox"
        aria-expanded={aperto && risultati.length > 0}
        aria-autocomplete="list"
        placeholder={stato === 'loading' ? 'Caricamento elenco risorse…' : 'Digita nome, cognome o email…'}
        disabled={stato === 'loading'}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setAperto(true);
          setEvidenziato(0);
          if (selectedId) onSelect(''); // testo cambiato: la scelta precedente non vale più
        }}
        onFocus={() => { if (query) setAperto(true); }}
        onBlur={() => { chiudiTimer.current = setTimeout(() => setAperto(false), 150); }}
        onKeyDown={onKeyDown}
      />
      {aperto && q && (
        <ul
          role="listbox"
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%', marginTop: 4,
            listStyle: 'none', padding: 4, background: 'var(--color-surface, #fff)',
            border: '1px solid var(--color-border, rgba(0,0,0,0.12))', borderRadius: 8,
            boxShadow: '0 6px 18px rgba(0,0,0,0.10)', maxHeight: 280, overflowY: 'auto',
          }}
        >
          {risultati.length === 0 ? (
            <li style={{ padding: '8px 10px', fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)' }}>
              Nessuna risorsa trovata per "{query.trim()}".
            </li>
          ) : risultati.map((c, i) => (
            <li
              key={c.consulente_id}
              role="option"
              aria-selected={i === evidenziato}
              onMouseDown={(e) => { e.preventDefault(); clearTimeout(chiudiTimer.current); scegli(c); }}
              onMouseEnter={() => setEvidenziato(i)}
              style={{
                padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                background: i === evidenziato ? 'var(--color-bg, #F1F0ED)' : 'transparent',
              }}
            >
              <div style={{ fontWeight: 600 }}>{c.nome || '—'}</div>
              {c.email && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>{c.email}</div>}
              {c.primaEsperienza && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>Proponibile, prima esperienza</div>
              )}
            </li>
          ))}
        </ul>
      )}
      {query.trim() && !selectedId && !aperto && stato === 'ready' && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #791F1F)', marginTop: 4 }}>
          Seleziona una risorsa dall'elenco.
        </div>
      )}
      {stato === 'error' && (
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #791F1F)', marginTop: 4 }}>
          Elenco risorse non disponibile. Ricarica la pagina per riprovare.
        </div>
      )}
    </div>
  );
}

const initialState = {
  consulente_id: '',
  ruolo: '',
  cliente_finale: '',
  denominazione_progetto: '',
  settore_l1: '',
  settore_l2: '',
  domini: [{ l1: '', l2: '' }],
  data_inizio: '',
  data_fine_prevista: '',
  capacita_garantita: '',
  attivita_svolte: '',
  referente_commerciale: '',
  tecnologie: '',
  certificazioni: '',
  note_libere: '',
  preavvisi_giorni: [...PREAVVISI_PROPOSTI],
};

function elenco(testo) {
  return (testo || '').split(',').map((v) => v.trim()).filter(Boolean);
}

function normalizzaTesto(t) {
  return (t || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/* Selettore a due livelli su una tassonomia { codice, nome, livello_2: [...] }.
   Se la tassonomia non è disponibile ripiega su due campi di testo, così il
   form resta usabile (il dato però non sarà filtrabile per codice). */
function SelettoreTassonomia({ id, etichetta, albero, stato, l1, l2, onChange, obbligatorio = true }) {
  const star = obbligatorio ? ' *' : '';
  if (stato !== 'ready' || !Array.isArray(albero) || albero.length === 0) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        <div>
          <label className="field-label" htmlFor={`${id}_l1`}>{etichetta} L1{star}</label>
          <input id={`${id}_l1`} className="field-input" value={l1} onChange={(e) => onChange(e.target.value, l2)} placeholder={stato === 'loading' ? 'Caricamento tassonomia…' : ''} />
        </div>
        <div>
          <label className="field-label" htmlFor={`${id}_l2`}>{etichetta} L2{star}</label>
          <input id={`${id}_l2`} className="field-input" value={l2} onChange={(e) => onChange(l1, e.target.value)} />
        </div>
      </div>
    );
  }
  const nodo = albero.find((x) => x.codice === l1);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
      <div>
        <label className="field-label" htmlFor={`${id}_l1`}>{etichetta} L1{star}</label>
        <select id={`${id}_l1`} className="field-input" value={l1} onChange={(e) => onChange(e.target.value, '')}>
          <option value="">Seleziona…</option>
          {albero.map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>
      </div>
      <div>
        <label className="field-label" htmlFor={`${id}_l2`}>{etichetta} L2{star}</label>
        <select id={`${id}_l2`} className="field-input" value={l2} disabled={!nodo} onChange={(e) => onChange(l1, e.target.value)}>
          <option value="">{nodo ? 'Seleziona…' : 'Scegli prima il livello 1'}</option>
          {(nodo?.livello_2 || []).map((x) => <option key={x.codice} value={x.codice}>{x.nome}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function EsperienzaOpenPage() {
  const kmClient = useKmClient('km');
  // /hr/sa-stato con lo stesso client della Console HR, dove è verificato.
  const realKmClient = useRealKmClient();
  const navigate = useNavigate();
  // Ruolo vero da /users/me, non il simulatore KM (fissato ad admin dal
  // 28/09): la UI deve mostrare solo ciò che il ruolo reale consente.
  const { user: realUser } = useRealAuth();
  const role = realUser?.role;
  const [searchParams] = useSearchParams();
  // Se si arriva dal pannello di una risorsa (link "+ Apri nuova esperienza
  // per questa risorsa"), l'id è già noto e univoco: precompilato e
  // bloccato, non serve più digitarlo o indovinarlo a mano.
  const consulenteIdDaLink = searchParams.get('consulente_id') || '';
  const [form, setForm] = useState(() => ({ ...initialState, consulente_id: consulenteIdDaLink }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [clientiEsistenti, setClientiEsistenti] = useState([]);
  const [duplicato, setDuplicato] = useState(null);
  const { filtri, stato: statoFiltri } = useFiltriEsperienze();
  const [consulenti, setConsulenti] = useState([]);
  const [consulentiStato, setConsulentiStato] = useState('loading'); // loading | ready | error

  useEffect(() => {
    // Suggerimento "cliente finale": nomi reali già usati in altre
    // esperienze, letti da GET /esperienze (già confermato). Fallimento
    // silenzioso: se non arriva, il campo resta libero, nessun blocco.
    kmClient
      .get('/esperienze')
      .then((data) => {
        const lista = Array.isArray(data?.esperienze) ? data.esperienze : [];
        const unici = [...new Set(lista.map((e) => e.cliente).filter(Boolean))].sort();
        setClientiEsistenti(unici);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Elenco consulenti per la ricerca risorsa (e per dare un nome
    // all'id arrivato dal link). Caricato una volta, filtrato in memoria.
    // 16/10 — UAT-12: consulenti esistenti + risorse Proponibili (R3) che
    // non hanno ancora un'anagrafica consulente.
    let annullato = false;
    async function carica() {
      const data = await realKmClient.get('/hr/sa-stato');
      const esistenti = Array.isArray(data?.consulenti) ? data.consulenti : [];
      const giaConsulenti = new Set(esistenti.map((c) => c.candidato_id).filter(Boolean));
      const proponibili = [];
      try {
        for (let offset = 0, pagine = 0; pagine < 10; pagine += 1, offset += 200) {
          const pagina = await realKmClient.get('/hr/sa-da-sollecitare', {
            query: { solo_da_sollecitare: false, limite: 200, offset },
          });
          const righe = Array.isArray(pagina?.risorse) ? pagina.risorse : [];
          righe
            .filter((r) => r.stato_ciclo_vita === 'R3' && r.candidato_id && !giaConsulenti.has(r.candidato_id))
            .forEach((r) => proponibili.push({
              consulente_id: `${PREFISSO_CANDIDATO}${r.candidato_id}`,
              candidato_id: r.candidato_id,
              nome: r.nome,
              email: r.email,
              primaEsperienza: true,
            }));
          if (!pagina?.ha_altre_pagine || righe.length < 200) break;
        }
      } catch {
        // Elenco Proponibili non disponibile: restano i consulenti esistenti.
      }
      const lista = [...esistenti, ...proponibili];
      lista.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'it'));
      return lista;
    }
    carica()
      .then((lista) => { if (!annullato) { setConsulenti(lista); setConsulentiStato('ready'); } })
      .catch(() => { if (!annullato) setConsulentiStato('error'); });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const consulenteDaLink = consulenteIdDaLink
    ? consulenti.find((c) => c.consulente_id === consulenteIdDaLink) || null
    : null;

  if (!canManageEsperienze(role)) {
    return (
      <div className="card state-empty">
        Solo HUB HR può aprire una nuova esperienza. Il tuo ruolo attuale non
        include questa azione (Specifica v1.1 §4.2).
      </div>
    );
  }

  const dominiValidi = form.domini.filter((d) => d.l1.trim() && d.l2.trim());
  const dateCoerenti = !form.data_inizio || !form.data_fine_prevista || form.data_fine_prevista >= form.data_inizio;
  const isValid = REQUIRED_FIELDS.every((f) => String(form[f]).trim() !== '')
    && dominiValidi.length > 0
    && dateCoerenti;

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'consulente_id' || field === 'denominazione_progetto' || field === 'cliente_finale') setDuplicato(null);
  }

  function updateDominio(i, l1, l2) {
    setForm((prev) => ({ ...prev, domini: prev.domini.map((d, j) => (j === i ? { l1, l2 } : d)) }));
  }

  function togglePreavviso(giorni) {
    setForm((prev) => ({
      ...prev,
      preavvisi_giorni: prev.preavvisi_giorni.includes(giorni)
        ? prev.preavvisi_giorni.filter((v) => v !== giorni)
        : [...prev.preavvisi_giorni, giorni],
    }));
  }

  /* Flusso alternativo 2 di UC-P03-01: esiste già un'esperienza in corso per
     la stessa combinazione risorsa-progetto? Il controllo è fatto qui prima
     del salvataggio; il vincolo va comunque garantito anche dal backend. */
  async function cercaDuplicato(consulenteId) {
    try {
      const data = await kmClient.get('/esperienze', {
        query: { consulente_id: consulenteId, stato: 'aperta', limite: 200 },
      });
      const lista = Array.isArray(data?.esperienze) ? data.esperienze : (Array.isArray(data) ? data : []);
      const progetto = normalizzaTesto(form.denominazione_progetto);
      const cliente = normalizzaTesto(form.cliente_finale);
      return lista.find((e) => normalizzaTesto(e.progetto) === progetto
        && (!e.cliente || normalizzaTesto(e.cliente) === cliente)) || null;
    } catch {
      return null; // controllo non disponibile: decide il backend
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // UAT-12: risorsa Proponibile senza anagrafica consulente → la si crea
      // ora (idempotente), come fa il pannello della risorsa.
      let consulenteId = form.consulente_id;
      if (consulenteId.startsWith(PREFISSO_CANDIDATO)) {
        const candidatoId = consulenteId.slice(PREFISSO_CANDIDATO.length);
        const promosso = await realKmClient.post(`/resources/${encodeURIComponent(candidatoId)}/consulente`);
        consulenteId = promosso.consulente_id;
      }
      const esistente = await cercaDuplicato(consulenteId);
      if (esistente) {
        setDuplicato(esistente);
        return;
      }
      const created = await kmClient.post('/esperienze/open', {
        consulente_id: consulenteId,
        ruolo: form.ruolo.trim(),
        cliente_finale: form.cliente_finale.trim(),
        denominazione_progetto: form.denominazione_progetto.trim(),
        settore_l1: form.settore_l1,
        settore_l2: form.settore_l2,
        dominio_l1: dominiValidi[0].l1,
        dominio_l2: dominiValidi[0].l2,
        domini_funzionali: dominiValidi.map((d) => ({ dominio_l1: d.l1, dominio_l2: d.l2 })),
        data_inizio: form.data_inizio,
        data_fine_prevista: form.data_fine_prevista,
        capacita_garantita: Number(form.capacita_garantita),
        attivita_svolte: form.attivita_svolte.trim(),
        referente_commerciale: form.referente_commerciale.trim(),
        tecnologie: elenco(form.tecnologie),
        certificazioni: elenco(form.certificazioni),
        preavvisi_giorni: [...form.preavvisi_giorni].sort((x, y) => y - x),
        note_libere: form.note_libere.trim() || null,
      });
      navigate(`/knowledge-management/esperienze/${created.esperienza_id}`);
    } catch (err) {
      // 409: possibile duplicato — non duplicare l'operazione (§10.2 scenario 6)
      setError(err?.detail || 'Non è stato possibile aprire l\'esperienza.');
    } finally {
      setSubmitting(false);
    }
  }

  const anteprima = anteprimaPromemoria(form.data_fine_prevista, form.preavvisi_giorni);

  return (
    <div>
      <h1 className="section-title">Apri nuova esperienza</h1>

      <form onSubmit={handleSubmit} className="card" style={{ display: 'grid', gap: 'var(--space-4)' }}>
        {error && <p className="state-error" style={{ textAlign: 'left', padding: 0 }}>{error}</p>}
        {duplicato && (
          <div className="badge badge-warning" style={{ display: 'block', padding: 'var(--space-3)', whiteSpace: 'normal', textAlign: 'left' }}>
            Esiste già un'esperienza in corso per questa risorsa sul progetto
            {' '}«{duplicato.progetto || form.denominazione_progetto}»
            {duplicato.cliente ? ` (${duplicato.cliente})` : ''}
            {duplicato.periodo ? `, periodo ${duplicato.periodo}` : ''}. Non ne è stata creata un'altra.
            {' '}
            <button type="button" className="btn-link" onClick={() => navigate(`/knowledge-management/esperienze/${duplicato.esperienza_id}`)}>
              Apri l'esperienza esistente →
            </button>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="consulente_id">Risorsa *</label>
          {consulenteIdDaLink ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                id="consulente_id"
                className="field-input"
                value={
                  consulenteDaLink?.nome
                  || (consulentiStato === 'loading' ? 'Caricamento…' : form.consulente_id)
                }
                title={form.consulente_id}
                readOnly
                style={{ background: 'var(--color-bg)', color: 'var(--color-ink-muted)' }}
              />
              <span className="badge badge-success" style={{ whiteSpace: 'nowrap' }}>collegata</span>
            </div>
          ) : (
            <RisorsaSearch
              consulenti={consulenti}
              stato={consulentiStato}
              selectedId={form.consulente_id}
              onSelect={(id) => update('consulente_id', id)}
            />
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <div>
            <label className="field-label" htmlFor="denominazione_progetto">Denominazione progetto *</label>
            <input id="denominazione_progetto" className="field-input" value={form.denominazione_progetto} onChange={(e) => update('denominazione_progetto', e.target.value)} required />
          </div>
          <div>
            <label className="field-label" htmlFor="cliente_finale">Cliente finale *</label>
            <input id="cliente_finale" className="field-input" list="clienti-esistenti" value={form.cliente_finale} onChange={(e) => update('cliente_finale', e.target.value)} required />
            <datalist id="clienti-esistenti">
              {clientiEsistenti.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
        </div>

        <SelettoreTassonomia
          id="settore"
          etichetta="Settore"
          albero={filtri?.settori}
          stato={statoFiltri}
          l1={form.settore_l1}
          l2={form.settore_l2}
          onChange={(l1, l2) => setForm((prev) => ({ ...prev, settore_l1: l1, settore_l2: l2 }))}
        />

        <div>
          <span className="field-label" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            Domini funzionali * <span style={{ fontWeight: 400, color: 'var(--color-ink-muted)' }}>(almeno uno, anche più di uno)</span>
          </span>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {form.domini.map((d, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--space-2)', alignItems: 'end' }}>
                <SelettoreTassonomia
                  id={`dominio_${i}`}
                  etichetta={`Dominio ${i + 1}`}
                  albero={filtri?.domini}
                  stato={statoFiltri}
                  l1={d.l1}
                  l2={d.l2}
                  obbligatorio={i === 0}
                  onChange={(l1, l2) => updateDominio(i, l1, l2)}
                />
                {form.domini.length > 1 && (
                  <button type="button" className="btn-link" onClick={() => setForm((prev) => ({ ...prev, domini: prev.domini.filter((_, j) => j !== i) }))}>
                    Rimuovi
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" className="btn-link" style={{ marginTop: 'var(--space-2)' }} onClick={() => setForm((prev) => ({ ...prev, domini: [...prev.domini, { l1: '', l2: '' }] }))}>
            + Aggiungi dominio funzionale
          </button>
        </div>

        <div>
          <label className="field-label" htmlFor="ruolo">Ruolo ricoperto *</label>
          <input id="ruolo" className="field-input" value={form.ruolo} onChange={(e) => update('ruolo', e.target.value)} required />
        </div>

        <div>
          <label className="field-label" htmlFor="attivita_svolte">Attività svolte *</label>
          <textarea id="attivita_svolte" className="field-textarea" value={form.attivita_svolte} onChange={(e) => update('attivita_svolte', e.target.value)} required />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
          <div>
            <label className="field-label" htmlFor="capacita_garantita">Capacità garantita % *</label>
            <input id="capacita_garantita" type="number" min="1" max="100" className="field-input" value={form.capacita_garantita} onChange={(e) => update('capacita_garantita', e.target.value)} required />
          </div>
          <div>
            <label className="field-label" htmlFor="data_inizio">Data inizio *</label>
            <input id="data_inizio" type="date" className="field-input" value={form.data_inizio} onChange={(e) => update('data_inizio', e.target.value)} required />
          </div>
          <div>
            <label className="field-label" htmlFor="data_fine_prevista">Data fine prevista *</label>
            <input id="data_fine_prevista" type="date" min={form.data_inizio || undefined} className="field-input" value={form.data_fine_prevista} onChange={(e) => update('data_fine_prevista', e.target.value)} required />
            {!dateCoerenti && (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #791F1F)', marginTop: 4 }}>
                La data di fine prevista non può precedere la data di inizio.
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <div>
            <label className="field-label" htmlFor="referente_commerciale">Referente commerciale *</label>
            <input id="referente_commerciale" className="field-input" value={form.referente_commerciale} onChange={(e) => update('referente_commerciale', e.target.value)} required />
          </div>
          <div>
            <span className="field-label">Tariffa applicata</span>
            <div className="field-input" style={{ background: 'var(--color-bg)', color: 'var(--color-ink-muted)', display: 'flex', alignItems: 'center' }}>
              Sola lettura — valorizzata dallo staffing
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
          <div>
            <label className="field-label" htmlFor="tecnologie">Tecnologie utilizzate (separate da virgola)</label>
            <input id="tecnologie" className="field-input" value={form.tecnologie} onChange={(e) => update('tecnologie', e.target.value)} />
          </div>
          <div>
            <label className="field-label" htmlFor="certificazioni">Certificazioni rilevanti (separate da virgola)</label>
            <input id="certificazioni" className="field-input" value={form.certificazioni} onChange={(e) => update('certificazioni', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="field-label" htmlFor="note_libere">Note libere</label>
          <textarea id="note_libere" className="field-textarea" value={form.note_libere} onChange={(e) => update('note_libere', e.target.value)} />
        </div>

        <div>
          <span className="field-label" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
            Preavvisi di reminder di chiusura (facoltativi, multipli)
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            {PREAVVISI.map((p) => (
              <label key={p.giorni} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)' }}>
                <input
                  type="checkbox"
                  checked={form.preavvisi_giorni.includes(p.giorni)}
                  onChange={() => togglePreavviso(p.giorni)}
                />
                {p.label}
              </label>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-faint)' }}>
              <input type="checkbox" checked disabled />
              Alla data prevista (obbligatorio, non disattivabile)
            </label>
          </div>
          {anteprima.length > 0 && (
            <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 18, fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
              {anteprima.map((v) => (
                <li key={v.giorni}>
                  {v.label}: {formatDataIt(v.data)}
                  {v.trascorso ? ' — data già trascorsa, non sarà emesso' : ''}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={!isValid || submitting} style={{ justifySelf: 'start' }}>
          {submitting ? 'Salvataggio…' : 'Salva'}
        </button>
      </form>
    </div>
  );
}
